import { observable, runInAction } from 'mobx';
import {
  IWasteGroupingContractServices,
  WasteGroupingContractModel,
  WasteGroupingContractSearchPayload,
} from 'services/RawData/wasteGroup_services';
import { message } from 'antd';

export type WasteGroupStore = {
  searchResponse: {
    data: WasteGroupingContractModel[];
    isSuccess: boolean;
    message: string;
    paging?: any;
  };
  detailResponse: {
    data?: WasteGroupingContractModel;
    isSuccess: boolean;
    message: string;
  };
  crudResponse: {
    isSuccess: boolean;
    message: string;
  };
};

export interface IWasteGroupPresenter {
  createStore(): WasteGroupStore;
  searchWasteGroups(store: WasteGroupStore, payload: WasteGroupingContractSearchPayload): Promise<void>;
  createWasteGroup(store: WasteGroupStore, payload: Partial<WasteGroupingContractModel>): Promise<void>;
  updateWasteGroup(store: WasteGroupStore, payload: Partial<WasteGroupingContractModel>): Promise<void>;
  deleteWasteGroup(store: WasteGroupStore, id: string): Promise<void>;
  getWasteGroupDetail(store: WasteGroupStore, id: string): Promise<void>;
  exportWasteGroups(payload: WasteGroupingContractSearchPayload): Promise<void>;
  importWasteGroups(file: File): Promise<void>;
}

export function createWasteGroupPresenter({
  wasteGroupingContractServices,
}: {
  wasteGroupingContractServices: IWasteGroupingContractServices;
}): IWasteGroupPresenter {
  return {
    createStore: () =>
      observable({
        searchResponse: {
          data: [],
          isSuccess: false,
          message: '',
          paging: undefined,
        },
        detailResponse: {
          data: undefined,
          isSuccess: false,
          message: '',
        },
        crudResponse: {
          isSuccess: false,
          message: '',
        },
      }),

    searchWasteGroups: async (store, payload) => {
      try {
        const result = await wasteGroupingContractServices.search(payload);
        runInAction(() => {
          store.searchResponse.data = result.data;
          store.searchResponse.isSuccess = result.isSuccess;
          store.searchResponse.message = result.message;
          store.searchResponse.paging = result.paging;
        });
      } catch (error) {
        console.error(error);
      }
    },

    createWasteGroup: async (store, payload) => {
      try {
        const result = await wasteGroupingContractServices.create(payload);
        runInAction(() => {
          store.crudResponse.isSuccess = result.isSuccess;
          store.crudResponse.message = result.message;
        });
        if (!result.isSuccess) {
          message.error(result.message);
        }
      } catch (error) {
        console.error(error);
      }
    },

    updateWasteGroup: async (store, payload) => {
      try {
        const result = await wasteGroupingContractServices.update(payload);
        runInAction(() => {
          store.crudResponse.isSuccess = result.isSuccess;
          store.crudResponse.message = result.message;
        });
        if (!result.isSuccess) {
          message.error(result.message);
        }
      } catch (error) {
        console.error(error);
      }
    },

    deleteWasteGroup: async (store, id) => {
      try {
        const result = await wasteGroupingContractServices.delete(id);
        runInAction(() => {
          store.crudResponse.isSuccess = result.isSuccess;
          store.crudResponse.message = result.message;
        });
      } catch (error) {
        console.error(error);
      }
    },

    getWasteGroupDetail: async (store, id) => {
      try {
        const result = await wasteGroupingContractServices.getDetail(id);
        runInAction(() => {
          store.detailResponse.data = result.data;
          store.detailResponse.isSuccess = result.isSuccess;
          store.detailResponse.message = result.message;
        });
      } catch (error) {
        console.error(error);
      }
    },

    exportWasteGroups: async (payload) => {
      try {
        await wasteGroupingContractServices.export(payload);
      } catch (error) {
        console.error(error);
      }
    },

    importWasteGroups: async (file) => {
      try {
        const result = await wasteGroupingContractServices.import(file);
        if (!result.isSuccess) {
          message.error(result.message);
        } else {
          message.success(result.message);
        }
      } catch (error) {
        console.error(error);
      }
    },
  };
}

